import { Component, DestroyRef, OnInit, ViewEncapsulation, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faMicrophone, faWandMagicSparkles, faCheck, faPenToSquare, faArrowRight, faArrowLeft } from '@fortawesome/free-solid-svg-icons'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'

import {
    ListItem,
    PendingChangeOrder,
    PendingChangeOrderExtractionResult,
    PendingChangeOrderInitiatedBy,
    PendingChangeOrderPricingMethod,
    PendingChangeOrderWorkType,
    RepositoryModal,
    createPendingChangeOrderInitiatedByListItems,
    parsePendingChangeOrderInitiatedByFromExtraction,
} from '../../shared'
import { VoiceTextFieldComponent } from '../../shared/components/voice-text-field/voice-text-field.component'
import { defaultVoiceAutoStartForPlatform } from '../../shared/components/voice-text-field/default-voice-auto-start'
import { PendingChangeOrdersComponent } from './pending-change-orders.component'

@Component({
    templateUrl: './pending-change-order-field-modal.component.html',
    styleUrls: ['./pending-change-order-field-modal.component.css'],
    standalone: true,
    imports: [FormsModule, FaIconComponent, WjInputModule, VoiceTextFieldComponent],
    encapsulation: ViewEncapsulation.None,
})
export class PendingChangeOrderFieldModalComponent extends RepositoryModal<PendingChangeOrder> implements OnInit {
    private destroyRef = inject(DestroyRef)

    parentComponent!: PendingChangeOrdersComponent

    micIcon = faMicrophone
    wandIcon = faWandMagicSparkles
    checkIcon = faCheck
    editIcon = faPenToSquare
    arrowRightIcon = faArrowRight
    arrowLeftIcon = faArrowLeft

    currentStep = 1

    /** Autostart dictation on wizard step 1 for mobile / touch-primary devices only. */
    readonly voiceAutoStart = defaultVoiceAutoStartForPlatform()
    finalText = ''

    isExtracting = false
    extractionError = ''

    jobOptions: ListItem<string>[] = []
    initiatedByOptions = createPendingChangeOrderInitiatedByListItems()

    ngOnInit() {
        this.parentComponent = this.editOptions.initializeParams.component as PendingChangeOrdersComponent

        this.jobOptions = this.parentComponent.jobMap

        if (this.isAdding) {
            this.target.createdOn = new Date()
            this.target.dateInitiated = new Date()
            this.target.initiatedBy = PendingChangeOrderInitiatedBy.Owner
            this.target.workType = PendingChangeOrderWorkType.CriticalPath
            this.target.pricingMethod = PendingChangeOrderPricingMethod.TM
            this.currentStep = 1
        } else {
            this.currentStep = 3
        }
    }

    goToStep(step: number) {
        this.currentStep = step
    }

    sendToAi() {
        this.isExtracting = true
        this.extractionError = ''

        this.services.data
            .post<PendingChangeOrderExtractionResult>(
                'api/PendingChangeOrder/ExtractFromTranscription',
                { transcription: this.finalText },
            )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: result => {
                    this.applyExtractionToTarget(result)
                    this.isExtracting = false
                    this.currentStep = 3
                },
                error: err => {
                    this.extractionError = err?.error?.error || 'An unexpected error occurred.'
                    this.isExtracting = false
                },
            })
    }

    private applyExtractionToTarget(result: PendingChangeOrderExtractionResult) {
        this.target.rawTranscription = this.finalText

        if (result.job) {
            const match = this.jobOptions.find(j => j.value && j.value === result.job)
            if (match) {
                this.target.job = match.value
            } else {
                const fuzzy = this.jobOptions.find(j => j.value && j.display?.toLowerCase().includes(result.job!.toLowerCase()))
                if (fuzzy) {
                    this.target.job = fuzzy.value
                } else {
                    this.target.job = result.job
                }
            }
        }

        this.target.description = result.description || ''
        this.target.notes = result.notes || ''

        const parsedInitiatedBy = parsePendingChangeOrderInitiatedByFromExtraction(result.initiatedBy)
        if (parsedInitiatedBy !== undefined) {
            this.target.initiatedBy = parsedInitiatedBy
        }
    }

    protected onBeforeSave(): boolean {
        if (!this.target.createdOn) {
            this.target.createdOn = new Date()
        }
        if (!this.target.dateInitiated) {
            this.target.dateInitiated = new Date()
        }
        this.target.initiatedBy ??= PendingChangeOrderInitiatedBy.Owner
        this.target.workType ??= PendingChangeOrderWorkType.CriticalPath
        this.target.pricingMethod ??= PendingChangeOrderPricingMethod.TM
        return true
    }

    protected onSave(resolveSave: (result?: any) => void, rejectSave: (reason?: any) => void) {
        const observable = this.isAdding || this.isDuplicating ? this.repositoryAdd() : this.repositoryEdit()
        observable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: successResult => {
                resolveSave(successResult)
            },
            error: errorResult => {
                rejectSave(errorResult)
            },
        })
    }
}
